import type { EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import type { IFamily } from '../../library/becks-family';
import type { GradeSize, PackageType, Trait, TreatmentType } from '../../shared/models/product.interface';
import * as ProductsActions from '../actions/products.actions';

type FamiliesState = EntityState<IFamily>;

export interface ProductsState {
  model: {
    families: FamiliesState;
    traits: Trait[];
    treatments: TreatmentType[];
    packageTypes: PackageType[];
    gradeSizes: GradeSize[];
  };
  viewModel: {
    familiesLoading: boolean;
    familiesLoaded: boolean;
    familiesLoadErred: boolean;
    traitsLoading: boolean;
    traitsLoaded: boolean;
    traitsLoadErred: boolean;
    treatmentsLoading: boolean;
    treatmentsLoaded: boolean;
    treatmentsLoadErred: boolean;
    packageTypesLoading: boolean;
    packageTypesLoaded: boolean;
    packageTypesLoadErred: boolean;
    gradeSizesLoading: boolean;
    gradeSizesLoaded: boolean;
    gradeSizesLoadErred: boolean;
  };
}

export const familiesAdapter = createEntityAdapter<IFamily>({
  selectId: (family: IFamily) => family.code,
});

const initialState: ProductsState = {
  model: {
    families: familiesAdapter.getInitialState(),
    traits: [],
    treatments: [],
    packageTypes: [],
    gradeSizes: [],
  },
  viewModel: {
    familiesLoading: false,
    familiesLoaded: false,
    familiesLoadErred: false,
    traitsLoading: false,
    traitsLoaded: false,
    traitsLoadErred: false,
    treatmentsLoading: false,
    treatmentsLoaded: false,
    treatmentsLoadErred: false,
    packageTypesLoading: false,
    packageTypesLoaded: false,
    packageTypesLoadErred: false,
    gradeSizesLoading: false,
    gradeSizesLoaded: false,
    gradeSizesLoadErred: false,
  },
};

export const productsStateKey = 'products';
export const productsReducer = createReducer(
  initialState,

  on(ProductsActions.loadProductFamilies, (state) => ({
    ...state,
    viewModel: {
      ...state.viewModel,
      familiesLoading: true,
      familiesLoaded: false,
      familiesLoadErred: false,
    },
  })),
  on(ProductsActions.loadProductFamiliesFailure, (state) => ({
    ...state,
    viewModel: {
      ...state.viewModel,
      familiesLoading: false,
      familiesLoaded: false,
      familiesLoadErred: true,
    },
  })),
  on(ProductsActions.loadProductFamiliesSuccess, (state, { data }) => ({
    ...state,
    model: {
      ...state.model,
      families: familiesAdapter.addMany(data, state.model.families),
    },
    viewModel: {
      ...state.viewModel,
      familiesLoading: false,
      familiesLoaded: true,
      familiesLoadErred: false,
    },
  })),

  on(ProductsActions.loadProductTraits, (state) => ({
    ...state,
    viewModel: { ...state.viewModel, traitsLoading: true, traitsLoaded: false, traitsLoadErred: false },
  })),
  on(ProductsActions.loadProductTraitsFailure, (state) => ({
    ...state,
    viewModel: { ...state.viewModel, traitsLoading: false, traitsLoaded: false, traitsLoadErred: true },
  })),
  on(ProductsActions.loadProductTraitsSuccess, (state, { data }) => ({
    ...state,
    model: { ...state.model, traits: data },
    viewModel: { ...state.viewModel, traitsLoading: false, traitsLoaded: true, traitsLoadErred: false },
  })),

  on(ProductsActions.loadProductTreatments, (state) => ({
    ...state,
    viewModel: { ...state.viewModel, treatmentsLoading: true, treatmentsLoaded: false, treatmentsLoadErred: false },
  })),
  on(ProductsActions.loadProductTreatmentsFailure, (state) => ({
    ...state,
    viewModel: { ...state.viewModel, treatmentsLoading: false, treatmentsLoaded: false, treatmentsLoadErred: true },
  })),
  on(ProductsActions.loadProductTreatmentsSuccess, (state, { data }) => ({
    ...state,
    model: { ...state.model, treatments: data },
    viewModel: { ...state.viewModel, treatmentsLoading: false, treatmentsLoaded: true, treatmentsLoadErred: false },
  })),

  on(ProductsActions.loadProductPackageTypes, (state) => ({
    ...state,
    viewModel: {
      ...state.viewModel,
      packageTypesLoading: true,
      packageTypesLoaded: false,
      packageTypesLoadErred: false,
    },
  })),
  on(ProductsActions.loadProductPackageTypesFailure, (state) => ({
    ...state,
    viewModel: {
      ...state.viewModel,
      packageTypesLoading: false,
      packageTypesLoaded: false,
      packageTypesLoadErred: true,
    },
  })),
  on(ProductsActions.loadProductPackageTypesSuccess, (state, { data }) => ({
    ...state,
    model: { ...state.model, packageTypes: data },
    viewModel: {
      ...state.viewModel,
      packageTypesLoading: false,
      packageTypesLoaded: true,
      packageTypesLoadErred: false,
    },
  })),

  on(ProductsActions.loadProductGradeSizes, (state) => ({
    ...state,
    viewModel: { ...state.viewModel, gradeSizesLoading: true, gradeSizesLoaded: false, gradeSizesLoadErred: false },
  })),
  on(ProductsActions.loadProductGradeSizesFailure, (state) => ({
    ...state,
    viewModel: { ...state.viewModel, gradeSizesLoading: false, gradeSizesLoaded: false, gradeSizesLoadErred: true },
  })),
  on(ProductsActions.loadProductGradeSizesSuccess, (state, { data }) => ({
    ...state,
    model: { ...state.model, gradeSizes: data },
    viewModel: { ...state.viewModel, gradeSizesLoading: false, gradeSizesLoaded: true, gradeSizesLoadErred: false },
  }))
);
